import { Component, Injector, Input, OnDestroy, ViewChild } from '@angular/core';

import { Observable, Subscription, combineLatest } from 'rxjs';

import { FilterService } from '../../../../../../Common/Filter/services/abstract-filter.service';
import { ButtonTypesEnum } from '../../../../../../Common/Forms/enums/button-types.enum';
import { TwitterAvatarSize } from '../../../../../../Common/Misc/pipes/TwitterAvatarSize/twitter-avatar-size.pipe';
import { CoreDialogService } from '../../../../../../Core/Dialogs/services/dialog.service';
import { TargetingResultModel } from '../../../../models/targeting-result.model';
import { TargetingListService } from '../../../../services/targeting-list.service';
import { TargetingSearchService } from '../../../../services/targeting-search.service';
import { RecipientViewerService } from './recipient-viewer-service';
import { FilterGroupModel } from '../../../../../../Common/Filter/models/filter-group.model';
import { OutletContactTabType, TargetingRhpContextsService } from '../../../../../../Common/TargetingShared/services/targeting-rhp-contexts.service';
import { AbstractDialogComponent } from '../../../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { DistributionRecipientModel } from '../../../../models/distribution-recipient-model';
import { EntityType } from '../../../../../../api/targeting/enum/entity.type.enum';
import { RecipientEditorService } from '../recipient-editor.service';
import { ExcludedRecipientApiModel } from '../../../../../../api/distributions/model/excluded-recipient.api.model';
import { FullProfileAnimationModel } from '../../../../../../Core/Dialogs/models/full-profile-animation.model';
import { FilterChipsComponent } from '../../../../../../Common/Filter/components/filterChips/filter-chips.component';
import { messageSeverityEnum } from '../../../../../../Common/Misc/enums/message-severity.enum';

enum RecipientViewerTab {
    recipientsTab,
    exclusionsTab
}

enum ExclusionsTab {
    contactsTab,
    outletsTab
}

@Component({
    selector: 'agpr-recipient-viewer',
    templateUrl: './recipient-viewer.component.html',
    styleUrls: [
        './recipient-viewer.component.scss',
    ],
    providers: [
        RecipientViewerService,
        {
            provide: 'PaginatorServiceInterface',
            useExisting: RecipientViewerService
        },
        {
            provide: FilterService,
            useExisting: RecipientViewerService
        },
        {
            provide: 'SortOrderServiceInterface',
            useExisting: RecipientViewerService
        },
    ],
    standalone: false
})

export class RecipientViewerComponent extends AbstractDialogComponent implements OnDestroy {
    private subscriptions: Subscription[] = [];
    public readonly recipientViewerTabs = RecipientViewerTab;

    recipient_: DistributionRecipientModel;
    @Input() set recipient(recipient: DistributionRecipientModel) {
        this.recipient_ = recipient;
        this.recipientViewerService.setRecipient(recipient);
    };
    @Input() overlayContainer: Element;
    @Input() animationTrigger: Observable<FullProfileAnimationModel>;

    @ViewChild('filterChipsComponent') public filterChipsComponent: FilterChipsComponent;

    hideSlideOverlay: boolean = false;

    excludedRecipients: ExcludedRecipientApiModel[];

    results: TargetingResultModel[];
    total: number;
    filteredTotal: number;
    pageNumber: number = 1;
    excludedTotal: number = 0;
    excludedContactTotal: number = 0;
    excludedOutletTotal: number = 0;

    chipsVisible: boolean;
    loading = true;
    sidebarExpanded: boolean = false;
    readonly twitterAvatarSizes = TwitterAvatarSize;
    readonly buttonTypes = ButtonTypesEnum;
    readonly entityTypes = EntityType;
    readonly messageSeverityEnum = messageSeverityEnum;

    previousRefine;
    refineQuery: string;
    selectedTabIndex: number;
    selectedExclusionsTabIndex: number;

    rhpSelectedTabIndex: number = 0;
    initialLoad: boolean = true;

    constructor(
        public recipientEditorService: RecipientEditorService,
        public recipientViewerService: RecipientViewerService,
        public coreDialogService: CoreDialogService,
        public targetingSearchService: TargetingSearchService,
        public targetingListService: TargetingListService,
        public targetingRhpContextsService: TargetingRhpContextsService,
        public injector: Injector,
    ) {
        super(coreDialogService, 'RecipientViewer');

        // we want to clear the contacts because we have to fetch the full list,
        // then select the first contact.. so if we toggle between lists,
        // we still see the old contact while fetching the list
        this.targetingRhpContextsService.clearContact();

        this.subscriptions.push(
            this.recipientViewerService.getResults().subscribe(results => {
                this.results = results;
                if (this.results?.length > 0) {
                    this.targetingSearchService.setFocusedResult(this.results[0]);
                    if (this.initialLoad) {
                        const tabToSwitch =
                            this.results[0].entityType === EntityType.CONTACT ? OutletContactTabType.contactProfile : OutletContactTabType.outletProfile;
                        this.targetingRhpContextsService.selectOutletContactTab(tabToSwitch);
                        this.initialLoad = false;
                    }
                } else {
                    this.targetingSearchService.setFocusedResult(null);
                }
            }),
            this.recipientViewerService.getFilters().subscribe(filters => {
                this.chipsVisible = !!filters.find(filter => {
                    if (filter instanceof FilterGroupModel) {
                        return filter.children.find(x => x.chips && x.chips.length > 0);
                    } else {
                        return filter.chips && filter.chips.length > 0;
                    }
                });
            }),
            this.recipientViewerService.getSearchLoading().subscribe(loading => this.loading = loading),
            this.recipientViewerService.getRecipientTotal().subscribe(total => this.total = total),
            this.recipientViewerService.getFilteredResultTotal().subscribe(total => this.filteredTotal = total),
            this.recipientViewerService.getPageCriteria().subscribe((pageCriteria) => {
                this.pageNumber = pageCriteria?.page;
            }),
            this.recipientEditorService.getExcludedRecipients().subscribe(excludedRecipients => {
                this.excludedRecipients = excludedRecipients;
            }),
            combineLatest([
                this.recipientViewerService.getContactExclusionsTotal(),
                this.recipientViewerService.getOutletExclusionsTotal()
            ]).subscribe(([contacts, outlets]) => {
                this.excludedContactTotal = contacts;
                this.excludedOutletTotal = outlets;
                this.excludedTotal = contacts + outlets;
            }),
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    tabChange(tabIndex: number) {
        this.selectedTabIndex = tabIndex;
        this.recipientViewerService.clearFilters();
        if (tabIndex === RecipientViewerTab.exclusionsTab) {
            if (this.excludedTotal === 0) {
                this.recipientViewerService.clearSearch();
            } else {
                this.recipientViewerService.viewExclusions = true;
                this.recipientViewerService.exclusionEntityType = this.excludedContactTotal > 0 ? EntityType.CONTACT : EntityType.OUTLET;
                this.recipientViewerService.doSearch(true, true);
            }
        } else {
            this.recipientViewerService.viewExclusions = false;
            this.recipientViewerService.doSearch(true, true);
        }
    }

    exclusionsTabChange(tabIndex: number) {
        this.selectedExclusionsTabIndex = tabIndex;
        this.recipientViewerService.clearFilters();
        if (tabIndex === ExclusionsTab.contactsTab) {
            this.recipientViewerService.exclusionEntityType = EntityType.CONTACT;
        } else {
            this.recipientViewerService.exclusionEntityType = EntityType.OUTLET;
        }
        this.recipientViewerService.doSearch(true, true);
    }

    cardFocused(entity: TargetingResultModel) {
        this.targetingSearchService.setFocusedResult(entity);
    }

    clearFilters() {
        this.recipientViewerService.clearFilters();
        this.recipientViewerService.applyFilters();
    }

    close(): void {
        this.recipientEditorService.updateRecipients();
        this.hideSlideOverlay = true;
        this.closeDialog();
    }
}
